import { useEffect, useState } from "react";
import apiCallService from "src/services/ApiCallService";
import ErrorUtils from "src/utils/ErrorUtils";
import CustomTag from "src/models/accounting-calendar/CustomTag";
import GetCalendarResult from "src/models/accounting-calendar/GetCalendarResult";
import { DeleteScope } from "src/models/accounting-calendar/CustomTagEnums";

export default class AccountingCalendarService {

    /**
     * Loads the accounting calendar for the given year. Re-fetches whenever `reloadKey`
     * changes (bumped after a create/update/delete) so the grid reflects mutations immediately.
     * @param year   4-digit year string, or undefined for the current year.
     * @param reloadKey monotonically-increasing key; changing it forces a refetch.
     */
    getCalendar(year: string | undefined, reloadKey: number): [GetCalendarResult, boolean, string] {
        const [result, setResult] = useState(null as unknown as GetCalendarResult);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiCallService.getCalendar(year);
                    const json = await response.json();
                    setResult(json as GetCalendarResult);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            fetchData();
        }, [year, reloadKey]);

        return [result, loading, error];
    }

    /** Creates a tag. Returns the created tag from the response. */
    async createTag(tag: CustomTag): Promise<CustomTag> {
        const response = await apiCallService.createCustomTag(tag);
        const json = await response.json();
        return json.tag as CustomTag;
    }

    /**
     * Updates a tag. Mirrors the delete contract: PUT /tags/{tagId} with dateValue (the one-time
     * tag's date) as a query param to locate its partition; recurring tags need no dateValue.
     * (Backend endpoint pending.)
     */
    async updateTag(tag: CustomTag): Promise<CustomTag> {
        const dateValue = tag.isRecurring ? undefined : tag.dateValue;
        const response = await apiCallService.updateCustomTag(tag.tagId as string, tag, dateValue);
        const json = await response.json();
        return json.tag as CustomTag;
    }

    /**
     * Deletes a tag.
     * @param tag         the tag to delete (uses its tagId + dateValue).
     * @param deleteScope SINGLE (one-time tag, or one occurrence of a recurring tag) or
     *                    RECURRENCE (the whole recurring rule).
     * @param targetMonth required (YYYY-MM) when removing a single occurrence of a recurring tag.
     */
    async deleteTag(tag: CustomTag, deleteScope: DeleteScope, targetMonth?: string): Promise<void> {
        // One-time tags need dateValue to locate the partition; recurring tags do not.
        const dateValue = tag.isRecurring ? undefined : tag.dateValue;
        await apiCallService.deleteCustomTag(tag.tagId as string, deleteScope, dateValue, targetMonth);
    }
}
